import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function CartDrawer() {
  const { items, cartOpen, setCartOpen, setQty, removeItem, total, setCheckoutOpen } =
    useCart();

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[75] bg-[#0A0A0B]/80 backdrop-blur-sm"
            onClick={() => setCartOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 260 }}
            className="fixed right-0 top-0 bottom-0 z-[76] w-full max-w-md bg-[#121215] border-l border-[#C5A059]/25 flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-[#C5A059]/20">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1] flex items-center gap-3">
                <ShoppingBag className="w-5 h-5 text-[#C5A059]" /> Carrello
              </h3>
              <button
                data-testid="cart-close-button"
                onClick={() => setCartOpen(false)}
                className="p-2 text-[#C0B9B0] hover:text-[#C5A059]"
                aria-label="Chiudi carrello"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {items.length === 0 && (
                <p data-testid="cart-empty" className="text-sm text-[#78746D] text-center pt-16 font-mono">
                  Il carrello è vuoto. Scopri il merch ufficiale.
                </p>
              )}
              {items.map((item) => (
                <div
                  key={`${item.id}-${item.size}-${item.color}`}
                  data-testid={`cart-item-${item.id}-${item.size}`}
                  className="flex gap-4 leather-card p-4"
                >
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-20 h-20 object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#F4EBE1] truncate">{item.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D] mt-1">
                      Taglia {item.size}{item.color ? ` · ${item.color}` : ""} · € {item.price.toFixed(2)}
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                      <button
                        data-testid={`qty-minus-${item.id}-${item.size}`}
                        onClick={() => setQty(item.id, item.size, item.color, item.qty - 1)}
                        className="w-7 h-7 border border-[#2A2A30] flex items-center justify-center text-[#C0B9B0] hover:border-[#C5A059]"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="font-mono text-sm text-[#F4EBE1]">{item.qty}</span>
                      <button
                        data-testid={`qty-plus-${item.id}-${item.size}`}
                        onClick={() => setQty(item.id, item.size, item.color, item.qty + 1)}
                        className="w-7 h-7 border border-[#2A2A30] flex items-center justify-center text-[#C0B9B0] hover:border-[#C5A059]"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        data-testid={`remove-item-${item.id}-${item.size}`}
                        onClick={() => removeItem(item.id, item.size, item.color)}
                        className="ml-auto text-[#78746D] hover:text-[#9E1B1B]"
                        aria-label="Rimuovi"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-[#C5A059]/20 space-y-4">
              <div className="flex justify-between items-center">
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#78746D]">
                  Subtotale
                </span>
                <span data-testid="cart-total" className="font-display text-2xl font-bold text-[#C5A059]">
                  € {total.toFixed(2)}
                </span>
              </div>
              <button
                data-testid="checkout-button"
                disabled={items.length === 0}
                onClick={() => {
                  setCartOpen(false);
                  setCheckoutOpen(true);
                }}
                className="w-full py-4 bg-[#C5A059] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#E2C787] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Procedi al Checkout
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
